import slugify from '@helpers/slugify';
import Link from '@atoms/Link/Link';
import ProductQuantityControl from '@molecules/ProductQuantityControl/ProductQuantityControl';
import {
  StyledAccent,
  StyledProductListItem,
  StyledPromotionText,
  StyledText,
  StyledTextWrapper,
  StyledWrapper,
} from './ProductListItem.styles';
import type {
  AxfoodBasicProductViewModel,
  AxfoodCartProductViewModel,
  AxfoodProductDetailsViewModel,
} from '@occ/api-client';
import { isCartProduct, isOnlineProduct } from '@helpers/typeGuards/product';
import { useProductImage } from '@hooks/useProductImage';
import { ReactElement } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';

interface Props {
  product: AxfoodProductDetailsViewModel | AxfoodBasicProductViewModel | AxfoodCartProductViewModel;
  quantity?: number;
  quantityCallback?: (quantity: number) => void;
  className?: string;
  clickable?: boolean;
  variant?: 'addToList' | 'default';
}

const ProductListItem = ({
  product,
  quantity,
  quantityCallback,
  className,
  clickable = true,
  variant = 'default',
}: Props) => {
  const { productImageURL, productImageAltText, onImageLoadError } = useProductImage(product);

  const { name, nicotineMedicalProduct, productLine2, potentialPromotions } = product;

  const cartProduct = isCartProduct(product)
    ? {
        totalDeposit: product.totalDeposit,
        totalPrice: product.totalPrice,
        totalDiscountedPriceWithDeposit: product.totalDiscountedPriceWithDeposit,
        appliedPromotions: product.appliedPromotions,
        promotionCartLabel: product.promotionCartLabel,
      }
    : null;

  const t = useTranslations('cart');
  const url = `${slugify(product.name)}-${product.code}`;
  const as = `/produkt/${encodeURI(url)}`;
  const hasAppliedPromotions = (cartProduct?.appliedPromotions?.length ?? 0) > 0;
  const isAddToList = variant === 'addToList';

  const generateContentWithLink = (content: ReactElement) => {
    if (clickable) {
      return (
        <Link href={as} scroll={false}>
          {content}
        </Link>
      );
    }

    return content;
  };

  return (
    <StyledProductListItem className={className}>
      <StyledWrapper>
        {generateContentWithLink(
          <Image
            src={productImageURL}
            alt={productImageAltText}
            width={isAddToList ? 50 : 40}
            height={isAddToList ? 50 : 40}
            onError={onImageLoadError}
            unoptimized
          />
        )}

        <StyledTextWrapper $variant={variant}>
          {generateContentWithLink(
            <StyledText type="subhead" size={isAddToList ? 'medium' : 'small'}>
              {name}
            </StyledText>
          )}
          <StyledText type="body" size={isAddToList ? 'small' : 'xsmall'} color="darkerGray">
            {productLine2}
          </StyledText>
          {isCartProduct(product) && cartProduct?.totalDeposit && (
            <StyledText type="detail" size="small" color="darkerGray">
              {cartProduct.totalDeposit} pant
            </StyledText>
          )}
          {isOnlineProduct(product) &&
            isCartProduct(product) &&
            cartProduct &&
            (hasAppliedPromotions ? (
              <StyledText type="body" color="red" size="xsmall">
                {cartProduct.totalDiscountedPriceWithDeposit}
              </StyledText>
            ) : (
              <StyledText type="body" size="xsmall">
                {cartProduct.totalPrice}
              </StyledText>
            ))}
        </StyledTextWrapper>
        <ProductQuantityControl
          product={product}
          variant={isAddToList ? 'wishlist' : 'minicart'}
          quantity={quantity}
          quantityCallback={quantityCallback}
        />
      </StyledWrapper>

      {nicotineMedicalProduct ? (
        <StyledPromotionText type="detail" color="darkerGray">
          {t('restriction.nicotineLabel')}
        </StyledPromotionText>
      ) : (
        isCartProduct(product) &&
        cartProduct?.promotionCartLabel &&
        potentialPromotions?.length > 0 && (
          <StyledPromotionText type="detail" color="darkerGray">
            <StyledAccent>{potentialPromotions[0].cartLabelFormatted}</StyledAccent>
            {cartProduct.promotionCartLabel}
          </StyledPromotionText>
        )
      )}
    </StyledProductListItem>
  );
};

export default ProductListItem;
